/**
 * Phase 9 — Canapé Function events editor.
 *
 * Sits alongside SpecialEventsPicker in the meal-plan day editor and
 * manages the slice of `mealPlan.special_events` where
 * `is_canape_function === true`. Library-driven special events stay
 * in the picker above; canapé functions are always free-text events
 * with one or more canapé items.
 *
 * Snapshot conventions match the backend `CanapeEventItem` model:
 *   * canape_id (optional)      → library reference
 *   * name (required)           → snapshot; library name pre-fills
 *   * quantity (int, ≥ 0)       → production count for this event
 *   * production_area_name      → may override the canapé default
 *   * notes                     → freeform per-item
 */
import { useMemo, useEffect, useState } from "react";
import client from "@/apiClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Cookie, Plus, X } from "lucide-react";

export default function CanapeFunctionsBlock({ value, onChange }) {
  // `value` = the FULL special_events list. We splice in/out canapé
  // events while preserving the order of library events.
  const list = useMemo(() => value || [], [value]);
  // Phase 12 — this block remains the preset shortcut for free-text
  // "Canapé Function" events (is_canape_function=True). Canapé items on
  // regular library special events live on the special event card itself
  // (rendered inside SpecialEventsPicker) to keep a single source of
  // truth in the UI. The PDF endpoints (canapé function PDF, filtered
  // PDF) broaden the criteria server-side to include regular events
  // with canape_items.
  const canapeEvents = useMemo(
    () => list.filter((e) => e.is_canape_function),
    [list],
  );

  // Canapé library — loaded once. Used both as a picker option list
  // and to grab snapshot fields when the user selects from the lib.
  const [canapesLib, setCanapesLib] = useState([]);
  useEffect(() => {
    let cancelled = false;
    client.get("/lib/canapes")
      .then((r) => { if (!cancelled) setCanapesLib(r.data || []); })
      .catch(() => { if (!cancelled) setCanapesLib([]); });
    return () => { cancelled = true; };
  }, []);

  const _newKey = () => (
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `cev_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
  );

  const addCanapeFunction = () => {
    onChange([
      ...list,
      {
        _key: _newKey(),
        is_canape_function: true,
        name: "Canapé Function",
        location_override: "",
        start: "",
        end: "",
        comment: "",
        canape_items: [],
      },
    ]);
  };

  // Replace the i-th canapé event (out of `canapeEvents`) in the full list.
  const updateCanapeEvent = (canapeIdx, patch) => {
    let seen = 0;
    onChange(list.map((e) => {
      if (!e.is_canape_function) return e;
      if (seen++ === canapeIdx) return { ...e, ...patch };
      return e;
    }));
  };
  const removeCanapeEvent = (canapeIdx) => {
    let seen = 0;
    onChange(list.filter((e) => {
      if (!e.is_canape_function) return true;
      const keep = seen++ !== canapeIdx;
      return keep;
    }));
  };

  const addCanapeItem = (canapeIdx) => {
    const evt = canapeEvents[canapeIdx];
    const next = [...(evt.canape_items || []), {
      _key: _newKey(), canape_id: null, name: "", quantity: 0,
      production_area_name: "", notes: "",
    }];
    updateCanapeEvent(canapeIdx, { canape_items: next });
  };

  const updateCanapeItem = (canapeIdx, itemIdx, patch) => {
    const evt = canapeEvents[canapeIdx];
    const next = (evt.canape_items || []).map((it, i) =>
      i === itemIdx ? { ...it, ...patch } : it
    );
    updateCanapeEvent(canapeIdx, { canape_items: next });
  };

  const removeCanapeItem = (canapeIdx, itemIdx) => {
    const evt = canapeEvents[canapeIdx];
    const next = (evt.canape_items || []).filter((_, i) => i !== itemIdx);
    updateCanapeEvent(canapeIdx, { canape_items: next });
  };

  const pickCanapeFromLib = (canapeIdx, itemIdx, libId) => {
    if (libId === "__custom__") {
      updateCanapeItem(canapeIdx, itemIdx, {
        canape_id: null, name: "", production_area_name: "",
      });
      return;
    }
    const lib = canapesLib.find((c) => c.id === libId);
    if (!lib) return;
    updateCanapeItem(canapeIdx, itemIdx, {
      canape_id: lib.id,
      name: lib.name,
      production_area_name: lib.production_area_name || "",
    });
  };

  return (
    <section
      className="rounded-lg border bg-card p-4"
      data-testid="canape-functions-block"
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2">
          <Cookie className="h-4 w-4 text-amber-700" />
          <h3 className="text-sm font-semibold">Canapé Functions</h3>
          <span className="text-xs text-muted-foreground">
            {canapeEvents.length === 0 ? "(none yet)" : `${canapeEvents.length} event(s)`}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={addCanapeFunction}
          data-testid="canape-fn-add-btn"
        >
          <Plus className="h-3.5 w-3.5" /> Add Canapé Function
        </Button>
      </div>

      {canapeEvents.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No canapé function events on this day. Add one to define the canapés the production team
          needs to prepare (with quantities, production areas, photos, etc.).
        </p>
      )}

      <div className="flex flex-col gap-3">
        {canapeEvents.map((evt, ci) => (
          <div
            key={evt._key || `canape-evt-${ci}`}
            className="rounded-md border bg-background p-3"
            data-testid={`canape-fn-card-${ci}`}
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <Input
                value={evt.name || ""}
                placeholder="Event name (e.g. Captain's Cocktail)"
                onChange={(e) => updateCanapeEvent(ci, { name: e.target.value })}
                className="flex-1"
                data-testid={`canape-fn-name-${ci}`}
              />
              <Button
                size="icon" variant="ghost"
                onClick={() => removeCanapeEvent(ci)}
                aria-label="Remove canapé function"
                data-testid={`canape-fn-remove-${ci}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
              <div className="space-y-1">
                <Label className="text-xs">Start</Label>
                <Input
                  value={evt.start || ""}
                  onChange={(e) => updateCanapeEvent(ci, { start: e.target.value })}
                  placeholder="HH:MM"
                  data-testid={`canape-fn-start-${ci}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">End</Label>
                <Input
                  value={evt.end || ""}
                  onChange={(e) => updateCanapeEvent(ci, { end: e.target.value })}
                  placeholder="HH:MM"
                  data-testid={`canape-fn-end-${ci}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Location</Label>
                <Input
                  value={evt.location_override || ""}
                  onChange={(e) => updateCanapeEvent(ci, { location_override: e.target.value })}
                  placeholder="Atrium DK4, Solarium, …"
                  data-testid={`canape-fn-loc-${ci}`}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Canapé items</Label>
                <Button
                  size="sm" variant="outline"
                  onClick={() => addCanapeItem(ci)}
                  data-testid={`canape-fn-${ci}-add-item-btn`}
                >
                  <Plus className="h-3.5 w-3.5" /> Add Canapé
                </Button>
              </div>
              {(evt.canape_items || []).map((it, ii) => {
                const lib = canapesLib.find((c) => c.id === it.canape_id);
                return (
                  <div
                    key={it._key || `c-item-${ii}`}
                    className="rounded border p-2 bg-muted/20"
                    data-testid={`canape-fn-${ci}-item-${ii}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {lib?.photo_url ? (
                        <img
                          src={lib.photo_url}
                          alt={lib.name}
                          className="h-10 w-10 rounded border object-cover"
                        />
                      ) : null}
                      <Select
                        value={it.canape_id || "__custom__"}
                        onValueChange={(v) => pickCanapeFromLib(ci, ii, v)}
                      >
                        <SelectTrigger
                          className="flex-1"
                          data-testid={`canape-fn-${ci}-item-${ii}-picker`}
                        >
                          <SelectValue placeholder="Pick a canapé or type a name…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__custom__">
                            (Type a one-off name)
                          </SelectItem>
                          {canapesLib.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => removeCanapeItem(ci, ii)}
                        aria-label="Remove canapé"
                        data-testid={`canape-fn-${ci}-item-${ii}-remove`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Input
                        value={it.name || ""}
                        placeholder="Canapé name"
                        onChange={(e) => updateCanapeItem(ci, ii, { name: e.target.value })}
                        data-testid={`canape-fn-${ci}-item-${ii}-name`}
                      />
                      <Input
                        type="number"
                        min={0}
                        value={Number.isFinite(it.quantity) ? it.quantity : 0}
                        onChange={(e) => updateCanapeItem(ci, ii, {
                          quantity: Math.max(0, parseInt(e.target.value || "0", 10) || 0),
                        })}
                        placeholder="Quantity"
                        data-testid={`canape-fn-${ci}-item-${ii}-qty`}
                      />
                      <Input
                        value={it.production_area_name || ""}
                        placeholder="Production area"
                        onChange={(e) => updateCanapeItem(ci, ii, {
                          production_area_name: e.target.value,
                        })}
                        data-testid={`canape-fn-${ci}-item-${ii}-area`}
                      />
                    </div>
                    <Textarea
                      value={it.notes || ""}
                      placeholder="Notes (VIP, allergens, …)"
                      onChange={(e) => updateCanapeItem(ci, ii, { notes: e.target.value })}
                      rows={1}
                      className="mt-1.5"
                      data-testid={`canape-fn-${ci}-item-${ii}-notes`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
